# Post a draft job ad

The SEEK API provides a mutation to post a job ads from an existing draft, [`postPositionFromDraft`](#postpositionfromdraft).
Posting typically takes less than a minute after which a [`PositionProfilePosted` event](#positionprofileposted) will be emitted.

Job ads on SEEK have a maximum period of 30 days.

## Idempotency

Requests that carry a job posting [mutation] must be [idempotent].
This removes the risk of unintentionally posting duplicate job ads when your software needs to [retry] a request due to an error.

A unique [`idempotencyId`] is required when posting a job ad.
We recommend that you generate a new [v4 UUID] before sending the first request to post each job ad, and to reuse that identifier on all subsequent retries of the same job ad.
This will ensure that we do not contact you unnecessarily about errored job ads that eventually succeed in going live on SEEK, as it provides a common thread across all attempts to post that job ad.

If your software has already posted a job ad with the same `idempotencyId`,
the SEEK API will return a [`PostPositionFromDraftPayload_Conflict`] result in accordance with our [mutation conflict] convention.
If your software was retrying a job posting request,
you can treat the conflict result as a success as it indicates the job ad has already been posted.
See the [code samples below](#operations) for practical examples of these conflict results.

The SEEK API deduplicates job ads posted by your software based on their `idempotencyId`s.
Your software should never reuse an `idempotencyId` when posting a different job ad;
this may result in subsequent job ads being missed or other unexpected behaviour.
Your identifiers are isolated and will not conflict with those generated by other recruitment software providers.

[`PostPositionFromDraftPayload_Conflict`]: /schema/named-type/PostPositionPayload_Conflict
[idempotent]: https://en.wikipedia.org/wiki/Idempotence#Computer_science_meaning
[mutation conflict]: /graphql/seek-api-conventions#mutation-conflicts
[mutation]: /graphql/in-practice#operations
[retry]: /graphql/error-responses#retrying-server-errors
[v4 UUID]: https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)

## Operations

The following sample mutations are provided for demonstration purposes only;
they are [not executable in the Playground environment](/graphql/playground).
You should use your live partner credentials to post a job ad under your [unsearchable SEEK test hirer](/introduction#integration-process) for end-to-end testing.

### postPositionFromDraft

The `postPositionFromDraft` mutation creates a new [position opening] and posts a [`PositionProfile`] in a single operation.

```graphql label="Mutation"
mutation (
  $id: String!
  $idempotencyId: String!
  $positionOrganizations: [String!]!
) {
  postPositionFromDraft(
    input: {
      draftPosition: {
        id: $id
        idempotencyId: $idempotencyId
        positionOrganizations: $positionOrganizations
      }
    }
  ) {
    ... on PostPositionFromDraftPayload_Success {
      positionOpening {
        documentId {
          value
        }
      }
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionFromDraftPayload_Conflict {
      conflictingPositionOpening {
        documentId {
          value
        }
      }
      conflictingPositionProfile {
        profileId {
          value
        }
      }
    }
  }
}
```

```json label="Variables"
{
  "id": "globalPublicTest:positionProfile:draft:seek:2dkY4vZJF",
  "idempotencyId": "e2d67780-a195-4576-891f-f7b5a326b07f",
  "positionOrganizations": ["seekAnzPublicTest:organization:seek:93WyyF1h"]
}
```

```jsonc label="Success result"
{
  "postPositionFromDraft": {
    // Details of the newly created position opening
    "positionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    },
    // Details of the newly created position profile
    "positionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb"
      }
    }
  }
}
```

```jsonc label="Conflict result"
{
  "postPositionFromDraft": {
    // Details of the conflicting position opening
    "conflictingPositionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    },
    // Details of the conflicting position profile
    "conflictingPositionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb"
      }
    }
  }
}
```

## Error handling

You may receive a [`BAD_USER_INPUT`] if the draft job ad is incomplete or contains invalid data.
In this case, your software should allow the hirer to return to the Enhanced Job Posting panel, passing in the `draftPositionProfileId` to allow them to correct any issues before attempting to post the job ad again.

[`BAD_USER_INPUT`]: /graphql/error-responses#bad_user_input

## Events

### PositionProfilePosted

You will receive events for SEEK hirers you have a [`JobPosting` relationship] with.

A [`PositionProfilePosted` event] is emitted when a job ad has been newly posted to the SEEK employment marketplace.
The [`PositionProfileUpdated` event] is emitted when an existing job ad is updated.

In response to these events you can [request fields from the position profile].
For example, you could query:

- The [`positionUri` field] to provide a hirer with a link to the live job ad.

- The [`postingInstructions.end` field] to retrieve the calculated end date if one wasn’t specified during posting.

[request fields from the position profile]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile

This is an example of a [webhook](/events/webhooks) body containing a `PositionProfilePosted` event:

```jsonc sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:UeFHntCu5giQh9Z6CRc2J7",
      "type": "PositionProfilePosted",
      "createDateTime": "2019-08-20T21:02:24.611Z",
      // This can be passed to the `positionProfile` query
      "positionProfileId": "seekAnzPublicTest:positionProfile:jobAd:26d9DVAzs",
      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:Kyak8JfPKhHCAXM2s9b9wX",
  "url": "https://example.com/1/2/3",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

## Copying or reposting

Providing the positionProfileId of an existing job will not copy or repost that job.
Instead, there are two ways you can implement posting a job based on a previous one.

A draft can be used to post multiple jobs.
Changes made in later drafts or updates to jobs will not be reflected in previous drafts.

You may [query the details of an existing job] and use these details to create the positionProfile details to pass to the panel.
While you will not need every field from `positionProfile`, the fields used will map 1:1 from `positionProfile` to `position.basePositionProfile`, with some exceptions as detailed below.

| `positionProfile` query result                                          | `position.basePositionProfile` panel input          |
| :---------------------------------------------------------------------- | :-------------------------------------------------- |
| `postingInstruction.branding.id.value`                                  | `postingInstructions.brandingId`                    |
| `jobCategories.id.value`                                                | `jobCategories.id`                                  |
| `positionLocation.id.value`                                             | `positionLocation.id`                               |
| `postingInstructions.applicationMethods.applicationUri.url` is not null | `applicationMethods[].selectedApplyType: 'linkout'` |
| `postingInstructions.applicationMethods.applicationUri.url` is null     | `applicationMethods[].selectedApplyType: 'soa'`     |

[`idempotencyId`]: /schema/named-type/CreatePostingInstructionInput#/field/idempotencyId
[`JobPosting` relationship]: /auth/hirer-relationships
[`PositionProfile`]: /schema/named-type/PositionProfile
[`PositionProfilePosted` event]: /schema/webhooks/PositionProfilePostedEvent
[`PositionProfileUpdated` event]: /schema/webhooks/PositionProfileUpdatedEvent
[`positionUri` field]: /schema/named-type/PositionProfile#/field/positionUri
[`postingInstructions.end` field]: /schema/named-type/PostingInstruction#/field/end
[position opening]: /use-cases/job-posting/position-openings
[query the details of an existing job]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad
